import { useCallback, useState } from "react";
import axios from "axios";
import { getAuthTokenFromCookie } from "../../config/apolloClient";
import { UploadError } from "./uploadError";
import {
  describeUploadRules,
  getAllowedUploadExtensions,
  getUploadFileRejection,
} from "../upload/uploadFileRules";

const API_REST_URL = window.__ENV__?.CONTROLLER_API_REST;
const UPLOAD_URL = `${API_REST_URL}/upload`;

const buildAuthHeaders = () => {
  const token = getAuthTokenFromCookie("ZORNI_AUTH_TOKEN");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Repère un UUID (8-4-4-4-12 hex) n'importe où dans la chaîne.
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Extracts the upload id (a UUID) from a file URL returned by the /upload
 * REST API, needed to PUT/DELETE an existing upload.
 *
 * The API has used two URL shapes over time:
 *  - ".../<uuid>.<ext>" (legacy, e.g. a Cloudinary-style link)
 *  - ".../upload/<uuid>[-v<version>]" (current, e.g.
 *    "https://controller-api-uat.mondoviva.com/upload/<uuid>-v0", with the
 *    "-vN" version suffix optional)
 * Matching the UUID pattern directly, instead of splitting on "/" and ".",
 * covers both without caring about an extension, a version suffix, or their
 * absence.
 */
export const getUploadIdFromUrl = (url) => {
  if (!url) return null;
  const match = String(url).match(UUID_PATTERN);
  return match ? match[0] : null;
};

// axios ne lève que sur un statut non-2xx : l'API renvoie désormais le vrai
// statut HTTP (400, 404, 413, 502, 504, 500…) avec un corps
// { code, error: { message, timestamp, correlationId } }, converti ici en
// UploadError. Un échec réseau (aucune réponse) l'est aussi.
const toUploadError = (error) => {
  if (error instanceof UploadError) return error;

  const response = error?.response;
  const body = response?.data;
  const apiError = body?.error ?? body;
  return new UploadError(
    (typeof apiError?.message === "string" && apiError.message) || error?.message || "Upload failed",
    {
      status: response?.status ?? body?.code,
      correlationId: apiError?.correlationId,
      cause: error,
    }
  );
};

const REJECTION_REASON_LABELS = {
  size: "Fichier trop volumineux.",
  extension: "Format non autorisé.",
  type: "Type de fichier non autorisé.",
};

// Dernier rempart avant l'envoi (les champs filtrent déjà à la sélection) : le
// même jeu de règles que le backend, sans aller-retour réseau. Refus = UploadError
// avec le statut que renverrait l'API (413 taille, 415 format ou contenu).
const assertFileAllowed = (file, resourceType, type) => {
  const extensions = getAllowedUploadExtensions(resourceType, type);
  const reason = getUploadFileRejection(file, extensions);
  if (!reason) return;

  throw new UploadError(
    `${REJECTION_REASON_LABELS[reason]} ${describeUploadRules(extensions)}`,
    { status: reason === "size" ? 413 : 415 }
  );
};

// Garde-fou contre un succès HTTP (2xx) qui n'en est pas un : l'ancienne version
// de l'API répondait 201 avec { code: 500, error } dans le corps. Tant que les
// deux côtés ne sont pas déployés ensemble, on ne fait donc jamais confiance au
// seul statut HTTP : une erreur dans le corps, ou un upload sans fileLink,
// interrompt l'appelant (sinon il enchaînerait ses appels GraphQL sans fichier).
const assertUploadSucceeded = (response, { requireFileLink }) => {
  const data = response?.data;
  const body = data && typeof data === "object" ? data : null;

  const hasErrorPayload =
    body && (body.error || (typeof body.code === "number" && (body.code < 200 || body.code >= 300)));
  if (hasErrorPayload) {
    throw new UploadError(body.error?.message || "Upload failed", {
      status: body.code ?? response.status,
      correlationId: body.error?.correlationId,
    });
  }

  if (requireFileLink && !body?.fileLink) {
    throw new UploadError("Invalid upload response: no file link returned.", {
      status: response?.status,
    });
  }
  return data;
};

/**
 * Mutualized /upload REST client (zorni-controller UploadController).
 *
 * Replaces 3 separate, duplicated implementations that each independently
 * called axios directly without ever attaching an Authorization header —
 * that endpoint requires auth like everything else, so every upload was
 * silently rejected with 401. Unlike GraphQL calls, /upload is plain REST
 * and never goes through Apollo Client's authLink (see apolloClient.js), so
 * the token has to be attached here explicitly, from the same
 * ZORNI_AUTH_TOKEN cookie.
 *
 * Also unlike the old implementations, this one does NOT swallow errors
 * (no catch-and-return-null): failures propagate to the caller so a
 * try/catch (and a toast) can actually react to them. Every failure — HTTP
 * error, network failure, or a 2xx carrying an error body — is thrown as an
 * UploadError (status, message, correlationId).
 */
export const useUpload = () => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(async (file, resourceType, type) => {
    if (!file || !type) {
      throw new Error("Le fichier et le type sont obligatoires.");
    }
    assertFileAllowed(file, resourceType, type);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("resources", resourceType);
    formData.append("type", type);

    setIsUploading(true);
    try {
      const response = await axios.post(UPLOAD_URL, formData, {
        headers: {
          ...buildAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      return assertUploadSucceeded(response, { requireFileLink: true });
    } catch (error) {
      throw toUploadError(error);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const updateFile = useCallback(async (uploadId, file, resourceType, type) => {
    if (!file || !type) {
      throw new Error("Le fichier et le type sont obligatoires.");
    }
    assertFileAllowed(file, resourceType, type);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("resources", resourceType);
    formData.append("type", type);

    setIsUploading(true);
    try {
      const response = await axios.put(`${UPLOAD_URL}/${uploadId}`, formData, {
        headers: {
          ...buildAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      return assertUploadSucceeded(response, { requireFileLink: true });
    } catch (error) {
      throw toUploadError(error);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const deleteFile = useCallback(async (uploadId) => {
    setIsUploading(true);
    try {
      const response = await axios.delete(`${UPLOAD_URL}/${uploadId}`, {
        headers: buildAuthHeaders(),
      });
      return assertUploadSucceeded(response, { requireFileLink: false });
    } catch (error) {
      throw toUploadError(error);
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    uploadFile,
    updateFile,
    deleteFile,
    getUploadIdFromUrl,
    isUploading,
  };
};
